/**
 * RanchWorks Offline Field Mode — local cache + pending-action queue.
 *
 * Goal: a provider out in the field with poor cell service can still
 *   - see today's + tomorrow's jobs (loaded from cache)
 *   - mark jobs done / no-show
 *   - record paid / partial / unpaid
 *   - add booking notes
 *
 * Brand-new bookings can NOT be created offline (per spec).
 *
 * Storage:
 *   localStorage.rw_offline_jobs    — cached field-view payload {data, cachedAt}
 *   localStorage.rw_pending_actions — array of queued mutations
 *
 * Each pending action:
 *   { id, kind, bookingId, payload, queuedAt, lastError? }
 *
 *   kind = 'patch_booking' | 'add_note'
 *   payload is the JSON body sent to PATCH /api/dashboard/bookings/{id}
 *     except `add_note` carries the new note text (we merge at sync time
 *     so we never overwrite notes added on other devices in between).
 */

import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CACHE_KEY = 'rw_offline_jobs';
const QUEUE_KEY = 'rw_pending_actions';

const safeParse = (s, fallback) => {
  try {
    return s ? JSON.parse(s) : fallback;
  } catch {
    return fallback;
  }
};

// ---- Cache ---------------------------------------------------------------

export function cacheFieldView(data) {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ data, cachedAt: new Date().toISOString() })
    );
  } catch {
    // ignore quota errors — cache is best-effort
  }
}

export function loadCachedFieldView() {
  const raw = localStorage.getItem(CACHE_KEY);
  const parsed = safeParse(raw, null);
  return parsed ? { ...parsed.data, _cachedAt: parsed.cachedAt } : null;
}

export function clearCachedFieldView() {
  localStorage.removeItem(CACHE_KEY);
}

// ---- Pending-action queue ------------------------------------------------

export function getPendingActions() {
  return safeParse(localStorage.getItem(QUEUE_KEY), []);
}

function writeQueue(actions) {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(actions));
}

export function queueAction(kind, bookingId, payload) {
  const action = {
    id: `act_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    kind,
    bookingId,
    payload,
    queuedAt: new Date().toISOString(),
  };
  writeQueue([...getPendingActions(), action]);
  return action;
}

export function removeAction(actionId) {
  writeQueue(getPendingActions().filter((a) => a.id !== actionId));
}

export function pendingCount() {
  return getPendingActions().length;
}

/**
 * Optimistically mutate the cached field-view payload so the UI reflects
 * the queued change immediately without waiting for sync. We mutate the
 * job objects in-place across today/tomorrow/thisWeek buckets.
 */
export function applyOptimisticPatch(bookingId, patch) {
  const cached = loadCachedFieldView();
  if (!cached) return null;
  const mutate = (arr) =>
    arr.map((j) => (j.id === bookingId ? { ...j, ...patch, _pendingSync: true } : j));
  const next = {
    ...cached,
    today: mutate(cached.today || []),
    tomorrow: mutate(cached.tomorrow || []),
    thisWeek: mutate(cached.thisWeek || []),
  };
  cacheFieldView(next);
  return next;
}

// ---- Sync engine ---------------------------------------------------------

/**
 * Flush every queued action to the backend. Returns
 *   { synced: number, failed: number, remaining: number }
 *
 * Failure handling:
 *   - 4xx (validation, not-found, conflict) → action is dropped from the
 *     queue with a one-time toast. Re-queuing a broken action is worse than
 *     dropping it, since the provider will see the latest data after sync.
 *   - Network / 5xx → action is kept; next sync retries.
 */
export async function syncPending(token, onProgress) {
  if (!token) return { synced: 0, failed: 0, remaining: pendingCount() };
  const actions = getPendingActions();
  if (actions.length === 0) return { synced: 0, failed: 0, remaining: 0 };

  let synced = 0;
  let failed = 0;
  const dropped = [];
  const keep = [];

  for (const a of actions) {
    onProgress?.(a);
    try {
      if (a.kind === 'patch_booking') {
        await axios.patch(`${API_URL}/api/dashboard/bookings/${a.bookingId}`, a.payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else if (a.kind === 'add_note') {
        // Refetch the current booking, then append the offline note to its
        // providerNotes so we never overwrite changes made elsewhere.
        const cur = await axios.get(`${API_URL}/api/dashboard/bookings/${a.bookingId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const merged = [
          (cur.data?.providerNotes || '').trim(),
          (a.payload?.note || '').trim(),
        ]
          .filter(Boolean)
          .join('\n');
        await axios.patch(
          `${API_URL}/api/dashboard/bookings/${a.bookingId}`,
          { providerNotes: merged },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // Unknown action kind — drop it.
        dropped.push(a);
        continue;
      }
      synced += 1;
    } catch (err) {
      const status = err?.response?.status;
      if (status && status >= 400 && status < 500) {
        // Permanent failure — drop it.
        failed += 1;
        dropped.push({ ...a, lastError: err?.response?.data?.detail || `HTTP ${status}` });
      } else {
        // Transient — keep for retry.
        keep.push({ ...a, lastError: err?.message || 'network error' });
      }
    }
  }

  writeQueue(keep);
  return { synced, failed, remaining: keep.length, dropped };
}
